import { useCallback, useEffect, useState } from 'react';
import { adminRequest, normalizeError } from '../api/adminApi';

const EMPTY_FORM = {
  googleId: '',
  name: '',
  enabled: true,
};

function AdminUsersView({ token, onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminRequest(token, '/api/admin/admin-user', { onUnauthorized });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(normalizeError(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, onUnauthorized]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const submit = async (event) => {
    event.preventDefault();
    setError(null);
    try {
      const endpoint = editId ? `/api/admin/admin-user/${editId}` : '/api/admin/admin-user';
      const method = editId ? 'PUT' : 'POST';
      await adminRequest(token, endpoint, {
        method,
        body: JSON.stringify(form),
        onUnauthorized,
      });
      setForm(EMPTY_FORM);
      setEditId(null);
      await loadItems();
    } catch (e) {
      setError(normalizeError(e));
    }
  };

  const beginEdit = (item) => {
    setEditId(item.id);
    setForm({
      googleId: item.googleId ?? '',
      name: item.name ?? '',
      enabled: Boolean(item.enabled),
    });
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete administrator?')) return;
    setError(null);
    try {
      await adminRequest(token, `/api/admin/admin-user/${id}`, {
        method: 'DELETE',
        onUnauthorized,
      });
      await loadItems();
    } catch (e) {
      setError(normalizeError(e));
    }
  };

  return (
    <div className="entities-scroller">
      <form className="entity-form" onSubmit={submit}>
        <h3>{editId ? 'Edit administrator' : 'Create administrator'}</h3>
        <label>
          Google ID
          <input
            value={form.googleId}
            onChange={(e) => setForm((prev) => ({ ...prev, googleId: e.target.value }))}
            required
          />
        </label>
        <label>
          Name
          <input
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          />
        </label>
        <label className="checkbox-line">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
          />
          Enabled
        </label>
        <div className="entity-form-actions">
          <button type="submit">{editId ? 'Save' : 'Create'}</button>
          {editId && (
            <button type="button" onClick={() => { setEditId(null); setForm(EMPTY_FORM); }}>
              Cancel edit
            </button>
          )}
        </div>
      </form>

      {loading && <p className="auth-hint">Loading…</p>}
      {error && <p className="auth-error">{error}</p>}
      {!loading && !error && (
        <table className="entities-table">
          <thead>
            <tr>
              <th>Google ID</th>
              <th>Name</th>
              <th>Enabled</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>{item.googleId}</td>
                <td>{item.name || '—'}</td>
                <td>{item.enabled ? 'Yes' : 'No'}</td>
                <td className="actions-cell">
                  <button type="button" onClick={() => beginEdit(item)}>Edit</button>
                  <button type="button" onClick={() => removeItem(item.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={4} className="empty-cell">No administrators found.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default AdminUsersView;
